import { Component, ReactNode } from 'react';
import { Alert, Button } from 'antd';
import styles from './ViewerErrorBoundary.module.css';

interface ViewerErrorBoundaryProps {
  children: ReactNode;
}

interface ViewerErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary to catch and display Cornerstone viewer initialization errors
 */
export class ViewerErrorBoundary extends Component<
  ViewerErrorBoundaryProps,
  ViewerErrorBoundaryState
> {
  constructor(props: ViewerErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<ViewerErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ViewerErrorBoundary]', error.message, error.stack);
    // eslint-disable-next-line no-console
    console.error(
      '[ViewerErrorBoundary] Component stack:',
      errorInfo.componentStack
    );
    this.setState({ error });
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <div className={styles.container}>
          <Alert
            message="Viewer Initialization Error"
            description={
              <div>
                <p>{error?.message || 'Failed to initialize viewer'}</p>
                <Button
                  type="primary"
                  onClick={this.handleRetry}
                  className={styles.retryButton}
                >
                  Retry
                </Button>
              </div>
            }
            type="error"
            showIcon
          />
        </div>
      );
    }

    return children;
  }
}
